import React, { useState } from 'react';
import { Form, Select } from 'antd';

const { Option } = Select;

/**
 * Reusable Searchable Select Field Component
 * Enhanced select with search functionality
 * 
 * @param {string} name - Field name
 * @param {string} label - Field label
 * @param {string} placeholder - Placeholder text
 * @param {Array} options - Array of options {value, label} or {id, name}
 * @param {boolean} required - Whether field is required
 * @param {boolean} readOnly - Whether field is read-only
 * @param {boolean} loading - Loading state
 * @param {boolean} allowClear - Allow clearing selection
 * @param {Array} rules - Additional validation rules
 * @param {function} onSearch - Search handler
 * @param {function} onSelect - Selection handler
 * @param {object} ...props - Additional props passed to Select
 */
const SearchableSelectField = ({ 
  name, 
  label, 
  placeholder, 
  options = [], 
  required = false, 
  readOnly = false,
  loading = false,
  allowClear = true,
  rules = [],
  onSearch,
  onSelect,
  ...props 
}) => {
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = (value) => {
    setSearchValue(value);
    onSearch?.(value);
  };

  const handleSelect = (value, option) => {
    setSearchValue('');
    onSelect?.(value, option);
  };

  const handleBlur = () => {
    setSearchValue('');
  };

  return (
    <Form.Item
      name={name}
      label={label}
      rules={required ? [{ required: true, message: `Please select ${label.toLowerCase()}` }, ...rules] : rules}
    >
      <Select 
        placeholder={placeholder}
        size="large"
        className="w-full rounded-lg"
        loading={loading}
        disabled={readOnly}
        allowClear={allowClear}
        showSearch
        searchValue={searchValue}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onBlur={handleBlur}
        filterOption={false}
        style={readOnly ? { cursor: 'not-allowed', backgroundColor: '#f9fafb' } : { backgroundColor: '#f9fafb' }}
        {...props}
      >
        {options.map(option => (
          <Option key={option.value || option.id} value={option.value || option.id}>
            {option.label || option.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

export default SearchableSelectField;
